/**
 * Поле замечания контроля качества — вместо поля ответа, пока включён режим выделения. Под полем —
 * тот же переключатель «Сообщение · Комментарий · В салон · Замечание», что под полем ответа.
 *
 * По умолчанию — голос (владелец, 26.10.26): крупный микрофон, «Стоп» и есть сохранение — кнопок
 * «Сохранить» в голосе нет; «Голос · Текст» — переключатель у заголовка, выбор помнится. В тексте —
 * заготовки фраз, поле и одна кнопка «Сохранить» (`Ctrl/Cmd+Enter`). Сверху поля — что сохранено за
 * этот заход: «✓ Сохранено №N · ко всему чату · расшифровывается…», затем текст расшифровки, — чтобы
 * было видно, что голос лёг в замечание, и не надиктовывать второй раз. Режим остаётся для
 * следующего замечания; выход — крестик, `Esc` или другой режим под полем.
 *
 * Отметки не обязательны: ничего не отмечено — замечание ко всему чату на момент записи. Голос —
 * по общим правилам `@/lib/voice`: после «Стоп» запись уходит сразу, расшифровка — фоном.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, LoaderCircle, Mic, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { useQc } from "@/components/qc/QcSelection";
import { useVoice, VoiceControl, voiceBusy } from "@/components/voice/VoiceInput";
import { chatRemarksKey, createRemark, MARKS, PHRASES, type QcMark, type QcRef, type QcRemark, uploadAudio } from "@/lib/qc";

const MARK_KEY = "prokat.qc.mark";
const INPUT_KEY = "prokat.qc.input";
const draftKey = (chatId: number) => `prokat.qc.draft.${chatId}`;
const read = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};
const write = (k: string, v: string) => {
  try {
    if (v) localStorage.setItem(k, v);
    else localStorage.removeItem(k);
  } catch {
    /* хранилище закрыто — черновик живёт только в поле */
  }
};
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/** «ко всему чату» / «к 2 сообщениям» / «к звонку» — к чему замечание. */
function scope(refs: QcRef[]): string {
  if (refs.length === 0) return "ко всему чату";
  const calls = refs.filter((r) => r.kind === "call").length;
  const msgs = refs.length - calls;
  const part = (n: number, one: string, few: string, many: string) => {
    const d = n % 10;
    const dd = n % 100;
    return `${n} ${d === 1 && dd !== 11 ? one : d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? few : many}`;
  };
  return `к ${[msgs && part(msgs, "сообщению", "сообщениям", "сообщениям"), calls && part(calls, "звонку", "звонкам", "звонкам")]
    .filter(Boolean)
    .join(" и ")}`;
}

export function RemarkComposer() {
  const qc = useQc();
  const client = useQueryClient();
  const [mark, setMark] = useState<QcMark>(() => (read(MARK_KEY) as QcMark) || "note");
  const [input, setInput] = useState<"voice" | "text">(() => (read(INPUT_KEY) === "text" ? "text" : "voice"));
  const [text, setText] = useState(() => (qc ? read(draftKey(qc.chatId)) : ""));
  // Голос, ушедший на сервер, пока не лёг в замечание (сохранение не прошло) — «Сохранить ещё раз»
  // приложит его же, запись не потеряется.
  const [pending, setPending] = useState<string | null>(null);
  // Сохранённые за этот заход — новые сверху; статус и расшифровку берём из списка замечаний чата.
  const [mine, setMine] = useState<QcRemark[]>([]);
  const voice = useVoice({
    onDone: async (blob, seconds) => {
      // Снимок на «Стоп»: оценка и отметки — одного момента, что бы ни меняли дальше.
      const snap = { mark, value: "", refs: [...(qc?.picked.values() ?? [])] };
      const audio = await uploadAudio(blob, seconds);
      setPending(audio);
      try {
        await save.mutateAsync({ audio, ...snap });
      } catch {
        // Ошибку покажет поле; голос уже на сервере и лежит в `pending`.
      }
    },
  });
  const key = useRef(newKey());
  const field = useRef<HTMLTextAreaElement>(null);
  const chatId = qc?.chatId ?? 0;

  useEffect(() => write(MARK_KEY, mark), [mark]);
  useEffect(() => write(INPUT_KEY, input === "text" ? "text" : ""), [input]);
  useEffect(() => write(draftKey(chatId), text), [chatId, text]);
  // На телефоне клавиатура сама не выскакивает: сначала отмечают сообщения, потом пишут.
  useEffect(() => {
    if (input === "text" && !window.matchMedia?.("(pointer: coarse)").matches) field.current?.focus();
  }, [input]);

  const save = useMutation({
    // Снимок формы на момент нажатия: что дописали за время отправки — не теряется.
    mutationFn: async ({ mark, value, audio, refs }: { mark: QcMark; value: string; audio: string | null; refs: QcRef[] }) => {
      const r = await createRemark({ chat_id: chatId, mark, text: value, audio, refs, client_key: key.current });
      return { r, value, audio };
    },
    onSuccess: ({ r, value, audio }) => {
      key.current = newKey();
      if (value) setText((now) => (now === value ? "" : now));
      if (value) write(draftKey(chatId), "");
      setPending((had) => (had === audio ? null : had));
      setMine((had) => [r, ...had.filter((x) => x.id !== r.id)]);
      void client.invalidateQueries({ queryKey: chatRemarksKey(chatId) });
      qc?.clear();
    },
  });

  // Свежее состояние своих замечаний — из общего списка чата (его опрашивает QcProvider); пока
  // голос расшифровывается, спрашиваем чаще.
  const fresh = mine.map((r) => qc?.remarks.find((x) => x.id === r.id) ?? r);
  const waiting = fresh.some((r) => r.status === "waiting_voice");
  useEffect(() => {
    if (!waiting) return;
    const t = window.setInterval(() => void client.invalidateQueries({ queryKey: chatRemarksKey(chatId) }), 3000);
    return () => window.clearInterval(t);
  }, [waiting, client, chatId]);

  const n = qc?.picked.size ?? 0;
  const recording = voiceBusy(voice.v);
  const unsent = !!voice.v.blob;
  const ready = (text.trim().length > 0 || !!pending) && !recording && !unsent && !save.isPending;
  const submit = () =>
    ready && save.mutate({ mark, value: input === "text" ? text : "", audio: pending, refs: [...(qc?.picked.values() ?? [])] });
  // Пока голос или сохранение уходят, из режима не выйти (см. `busy` в QcSelection).
  const busy = recording || save.isPending;
  const setBusy = qc?.setBusy;
  useEffect(() => setBusy?.(busy), [busy, setBusy]);
  useEffect(() => () => setBusy?.(false), [setBusy]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) qc?.stop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qc, busy]);

  if (!qc) return null;
  const idle = voice.v.state === "idle" || voice.v.state === "error";
  return (
    <div
      className="rounded-[18px] bg-[var(--tg-surface)] px-3 py-2.5 shadow-[0_1px_8px_1px_rgba(0,0,0,.12)]"
      data-annot="поле замечания контроля качества"
    >
      {fresh.length > 0 && (
        <ul className="mb-2 flex flex-col gap-1 border-b border-black/10 pb-2" aria-live="polite" data-annot="сохранённые замечания">
          {fresh.slice(0, 3).map((r) => (
            <li key={r.id} className="flex min-w-0 items-start gap-2 text-[13px] leading-[18px]">
              <CircleCheck size={16} className="mt-px flex-none text-[#2e9e4f]" />
              <div className="min-w-0 flex-1">
                <div>
                  <span className="font-medium">Сохранено №{r.id}</span>
                  <span className="text-[var(--tg-sec)]"> · {scope(r.refs)} · </span>
                  {r.status === "waiting_voice" ? (
                    <span className="inline-flex items-center gap-1 text-[var(--tg-sec)]">
                      <LoaderCircle size={12} className="animate-spin motion-reduce:animate-none" /> расшифровывается…
                    </span>
                  ) : (
                    <span className="text-[var(--tg-sec)]">{r.status_text.toLowerCase()}</span>
                  )}
                </div>
                {(r.transcript || r.text) && (
                  <div className="line-clamp-2 text-[var(--tg-sec)]">«{r.transcript || r.text}»</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => qc.open(r.id)}
                className="flex-none text-[12px] font-medium text-[var(--tg-primary)] hover:underline"
                title="Открыть во вкладке «Замечания» — изменить, удалить"
              >
                Открыть
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mb-2 flex items-center gap-2 text-[13px]">
        <span className="min-w-0 font-medium">
          {n ? `Замечание ${scope([...qc.picked.values()])}` : "Замечание ко всему чату"}
          <span className="font-normal text-[var(--tg-sec)]">{n ? "" : " — или отметьте сообщения слева"}</span>
        </span>
        <div className="ml-auto flex flex-none rounded-full bg-[var(--tg-hover)] p-0.5" role="radiogroup" aria-label="Как записать замечание">
          {(
            [
              ["voice", "Голос"],
              ["text", "Текст"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={input === id}
              disabled={busy}
              onClick={() => setInput(id)}
              className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium disabled:opacity-50 ${
                input === id ? "bg-[var(--tg-surface)] text-[var(--tg-text)] shadow-sm" : "text-[var(--tg-sec)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => qc.stop()}
          disabled={busy}
          title={busy ? "Замечание ещё сохраняется — подождите несколько секунд" : "Выйти из режима замечаний (Esc); черновик текста останется"}
          className="grid size-7 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)] disabled:opacity-40"
        >
          <X size={17} />
        </button>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Оценка">
        {MARKS.map((m) => (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={mark === m.id}
            onClick={() => setMark(m.id)}
            title={m.title}
            className={`rounded-full px-3 py-1 text-[13px] font-medium ${
              mark === m.id
                ? m.id === "good"
                  ? "bg-[#2e9e4f] text-white"
                  : m.id === "bad"
                    ? "bg-[#e53935] text-white"
                    : "bg-[var(--tg-primary)] text-white"
                : "bg-[var(--tg-hover)] text-[var(--tg-text)] hover:bg-black/10"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      {input === "voice" ? (
        <div className="flex min-h-[64px] items-center gap-3" data-annot="голосовой ввод замечания">
          {idle && !pending ? (
            <>
              <button
                type="button"
                onClick={() => void voice.start()}
                className="grid size-14 flex-none place-items-center rounded-full bg-[var(--tg-primary)] text-white shadow-[var(--tg-shadow)] hover:opacity-90"
                title="Надиктовать замечание (до 4 минут)"
                aria-label="Надиктовать замечание"
                data-annot="надиктовать голосом"
              >
                <Mic size={26} />
              </button>
              <div className="min-w-0 text-[13px] leading-[18px]">
                <div className="font-medium">{fresh.length ? "Надиктовать ещё одно" : "Нажмите и говорите"}</div>
                <div className="text-[var(--tg-sec)]">После «Стоп» замечание сохранится само, текст расшифруется фоном</div>
                {voice.v.state === "error" && <div className="text-[#e53935]">{voice.v.error}</div>}
              </div>
            </>
          ) : pending && idle ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-[13px]">
              <span>Голос загружен, но замечание не сохранилось.</span>
              <button
                type="button"
                onClick={submit}
                disabled={!ready}
                className="min-h-9 rounded-full bg-[var(--tg-primary)] px-4 py-1.5 font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                {save.isPending ? "Сохраняю…" : "Сохранить ещё раз"}
              </button>
              <button
                type="button"
                onClick={() => setPending(null)}
                className="min-h-9 rounded-full px-3 py-1.5 text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
              >
                Не сохранять
              </button>
            </div>
          ) : (
            <VoiceControl voice={voice} disabled={save.isPending} />
          )}
        </div>
      ) : (
        <>
          <div className="mb-2 flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none]" aria-label="Заготовки фраз">
            {PHRASES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setText((had) => (had.trim() ? `${had.trimEnd()}. ${p}` : p));
                  field.current?.focus();
                }}
                className="flex-none whitespace-nowrap rounded-full border border-black/10 px-2 py-0.5 text-[12px] text-[var(--tg-sec)] hover:border-[var(--tg-primary)] hover:text-[var(--tg-primary)]"
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <textarea
              ref={field}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={2}
              placeholder="Что произошло и как должно быть"
              className="min-w-0 flex-1 resize-y rounded-[12px] border border-black/10 bg-transparent px-3 py-2 text-[15px] leading-[20px] outline-none focus:border-[var(--tg-primary)] max-md:text-[16px]"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!ready}
              title="Сохранить замечание (Ctrl+Enter)"
              className="min-h-9 flex-none rounded-full bg-[var(--tg-primary)] px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-40"
            >
              {save.isPending ? "Сохраняю…" : "Сохранить"}
            </button>
          </div>
        </>
      )}
      {save.isError && <p className="mt-1.5 text-[13px] text-[#e53935]">{save.error.message}</p>}
    </div>
  );
}

/**
 * Низ ленты: в режиме замечаний — поле замечания, обычное поле ответа скрыто, но живо (набранный
 * ответ, файлы и цитата переживают серию замечаний). `display: contents` — раскладка не меняется.
 */
export function QcComposeSwitch({
  className,
  bar,
  pickBar,
  children,
}: {
  className: string;
  bar?: ReactNode;
  /** Плашка действий с пачкой отмеченных сообщений (режим выделения `pick`). */
  pickBar?: ReactNode;
  children: ReactNode;
}) {
  const qc = useQc();
  return (
    <>
      {qc?.active && qc.purpose === "remark" && (
        <div className={className}>
          <RemarkComposer />
          {bar}
        </div>
      )}
      {qc?.active && qc.purpose === "pick" && <div className={className}>{pickBar}</div>}
      <div hidden={!!qc?.active} className="contents">
        {children}
      </div>
    </>
  );
}
